// auth0-spa-js's cache for the portal in a build with multi-resource refresh tokens (AB#5741).
//
// Entries stay in localStorage exactly as auth0-spa-js's own localStorage cache keeps them:
// `@@auth0spajs@@::<clientId>::<audience>::<scope>` as `{ body, expiresAt }`, and the ID token under
// `@@auth0spajs@@::<clientId>::@@user@@`. The cross-tab account checks (auth/crossTabAccount) and the
// cached-token check (auth/cachedAccessToken) read them there. So does any tab still running an older
// bundle, and so would a rollback.
//
// The refresh token those entries share is also committed to the refresh-token store
// (auth/refreshTokenStore), and the token handed to the SDK is always the store's. Under the token lock the
// store is coherent across tabs; a tab's copy of localStorage is not. An entry's own copy of the token is
// offered to the store when read, and the store takes it only if it never saw it, which is only the case
// for a token written by an older bundle. If IndexedDB is unavailable, this tab uses the entries' tokens as
// before.
import type { Cacheable, ICache } from '@auth0/auth0-react';
import { createIndexedDbRefreshTokenStore, RefreshTokenStore } from './refreshTokenStore';

const CACHE_KEY_PREFIX = '@@auth0spajs@@';
const ID_TOKEN_KEY_SUFFIX = '::@@user@@';

type JsonObject = Record<string, unknown>;

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const refreshTokenOf = (entry: unknown) => {
  const body = isJsonObject(entry) && isJsonObject(entry.body) ? entry.body : undefined;
  return typeof body?.refresh_token === 'string' && body.refresh_token ? body.refresh_token : undefined;
};

const withRefreshToken = (entry: JsonObject, token: string | undefined) => {
  const { refresh_token: _copy, ...body } = entry.body as JsonObject;
  return { ...entry, body: token ? { ...body, refresh_token: token } : body };
};

const clientIdOf = (key: string) => key.split('::')[1] ?? '';

let hasWarnedStoreUnavailable = false;
const warnStoreUnavailable = (error: unknown) => {
  if (hasWarnedStoreUnavailable) return;

  hasWarnedStoreUnavailable = true;
  console.warn('[auth] The refresh-token store (IndexedDB) is unavailable, so this tab uses the refresh token in localStorage.', error);
};

export interface PortalAuth0CacheOptions {
  storage?: () => Storage;
  refreshTokens?: RefreshTokenStore;
}

export const createPortalAuth0Cache = ({
  storage = () => window.localStorage,
  refreshTokens = createIndexedDbRefreshTokenStore(),
}: PortalAuth0CacheOptions = {}): ICache => {
  const readEntry = (key: string): unknown => {
    const json = storage().getItem(key);
    if (!json) return undefined;

    try {
      return JSON.parse(json);
    } catch {
      return undefined;
    }
  };

  return {
    async set<T = Cacheable>(key: string, entry: T) {
      const token = refreshTokenOf(entry);
      // The token is committed before the entry that carries it is written.
      if (token) await refreshTokens.offer(clientIdOf(key), token).catch(warnStoreUnavailable);
      storage().setItem(key, JSON.stringify(entry));
    },

    async get<T = Cacheable>(key: string): Promise<T | undefined> {
      const entry = readEntry(key);
      const copy = refreshTokenOf(entry);
      if (!copy || !isJsonObject(entry)) return entry as T | undefined;

      try {
        const clientId = clientIdOf(key);
        let token = await refreshTokens.read(clientId);
        if (token !== copy) {
          // A copy the store never saw was written by an older bundle: take it unless the store replaced it.
          await refreshTokens.offer(clientId, copy);
          token = await refreshTokens.read(clientId);
        }
        // No token means the store cleared this one at sign-out, or replaced it and holds none: never hand it out.
        return withRefreshToken(entry, token) as T;
      } catch (error) {
        warnStoreUnavailable(error);
        return entry as T;
      }
    },

    async remove(key: string) {
      storage().removeItem(key);
      // The ID token goes only with the whole local session (sign-out, or a sign-in as someone else), and the
      // refresh token goes with it.
      if (key.endsWith(ID_TOKEN_KEY_SUFFIX)) await refreshTokens.clear(clientIdOf(key)).catch(warnStoreUnavailable);
    },

    allKeys: () => {
      const store = storage();
      const keys: string[] = [];
      for (let index = 0; index < store.length; index++) {
        const key = store.key(index);
        if (key?.startsWith(CACHE_KEY_PREFIX)) keys.push(key);
      }
      return keys;
    },
  };
};

let portalAuth0Cache: ICache | undefined;

/**
 * The cache settings for every Auth0Provider on the page. With MRRT, the portal cache, shared by them. Without,
 * auth0-spa-js's own localStorage cache, unchanged: each entry may then hold a refresh token for its own
 * audience, which one store per client could not keep apart.
 */
export const auth0CacheProps = (usesMultiResourceRefreshTokens: boolean) => usesMultiResourceRefreshTokens
  ? { cache: (portalAuth0Cache ??= createPortalAuth0Cache()) }
  : { cacheLocation: 'localstorage' as const };
